import React, { useRef, useEffect, useState } from 'react';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Lato:wght@300;400;700;900&display=swap');

  :root {
    --russian-red:  #CD0302;
    --sunburst:     #F0C400;
    --edam:         #F5D19D;
    --citrusy:      #F88702;
    --green:        #4F7E17;
    --green-light:  #6aad1f;
    --edam-light:   #fdf3e3;
    --edam-dark:    #e8c070;
    --soil:         #3d2b1f;
    --cream:        #fffdf5;
    --fog:          #f7f2ea;
  }

  .rp-root {
    font-family: 'Lato', sans-serif;
    background:
      radial-gradient(ellipse at 5% 0%, rgba(79,126,23,0.09) 0%, transparent 50%),
      radial-gradient(ellipse at 95% 100%, rgba(240,196,0,0.08) 0%, transparent 50%),
      var(--cream);
    min-height: 100vh;
    padding: 40px 16px 72px;
    color: var(--soil);
  }

  .rp-inner {
    max-width: 840px;
    margin: 0 auto;
  }

  /* ── Header ── */
  .rp-header {
    margin-bottom: 40px;
    padding-bottom: 28px;
    border-bottom: 2px dashed var(--edam-dark);
    position: relative;
  }

  .rp-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: var(--green);
    color: #fff;
    font-size: 0.68rem;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 5px 14px;
    border-radius: 20px;
    margin-bottom: 16px;
  }

  .rp-title {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 700;
    font-style: italic;
    color: var(--soil);
    line-height: 1.15;
    margin-bottom: 10px;
  }

  .rp-title span { color: var(--green); }

  .rp-updated {
    font-size: 0.82rem;
    color: #9a8878;
    font-style: italic;
    letter-spacing: 0.03em;
  }

  .rp-header-deco {
    position: absolute;
    right: 0; top: 0;
    width: 90px; height: 90px;
    background: radial-gradient(circle, rgba(240,196,0,0.18) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }

  /* ── Sections ── */
  .rp-section {
    margin-bottom: 32px;
    padding-bottom: 32px;
    border-bottom: 1px dashed var(--edam-dark);
  }

  .rp-section:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .rp-section-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 18px;
  }

  .rp-section-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
  }

  .rp-section-icon.green  { background: rgba(79,126,23,0.12);  color: var(--green); }
  .rp-section-icon.orange { background: rgba(248,135,2,0.12);  color: var(--citrusy); }
  .rp-section-icon.red    { background: rgba(205,3,2,0.10);    color: var(--russian-red); }
  .rp-section-icon.yellow { background: rgba(240,196,0,0.18);  color: #b08c00; }

  .rp-section-title {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--soil);
  }

  /* ── Intro Strip ── */
  .rp-intro-strip {
    background: linear-gradient(135deg, rgba(79,126,23,0.07) 0%, rgba(240,196,0,0.07) 100%);
    border-left: 4px solid var(--green);
    border-radius: 0 14px 14px 0;
    padding: 20px 22px;
    font-size: 0.97rem;
    color: #5a4a3a;
    line-height: 1.75;
  }

  /* ── Timeline strip ── */
  .rp-timeline-strip {
    background: var(--fog);
    border: 1.5px solid var(--edam-dark);
    border-radius: 14px;
    padding: 18px 22px;
    font-size: 0.95rem;
    color: #5a4a3a;
    line-height: 1.75;
  }

  /* ── Condition cards ── */
  .rp-conditions { display: flex; flex-direction: column; gap: 10px; }

  .rp-condition {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 14px 18px;
    border-radius: 12px;
    border: 1.5px solid transparent;
    transition: transform 0.15s, box-shadow 0.15s;
  }

  .rp-condition:hover {
    transform: translateX(3px);
    box-shadow: 0 3px 12px rgba(0,0,0,0.06);
  }

  .rp-condition.allowed {
    background: rgba(79,126,23,0.05);
    border-color: rgba(79,126,23,0.18);
  }

  .rp-condition.denied {
    background: rgba(205,3,2,0.04);
    border-color: rgba(205,3,2,0.18);
    border-left: 4px solid var(--russian-red);
    padding-left: 15px;
  }

  .rp-condition-icon {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    font-weight: 900;
    margin-top: 1px;
  }

  .allowed .rp-condition-icon { background: var(--green); color: #fff; }
  .denied  .rp-condition-icon { background: var(--russian-red); color: #fff; }

  .rp-condition-text {
    font-size: 0.92rem;
    color: #5a4a3a;
    line-height: 1.65;
  }

  /* ── Process card ── */
  .rp-process-card {
    background: var(--fog);
    border: 1.5px solid var(--edam-dark);
    border-radius: 14px;
    padding: 20px 22px;
    font-size: 0.95rem;
    color: #5a4a3a;
    line-height: 1.75;
    margin-bottom: 14px;
  }

  .rp-note-card {
    background: rgba(240,196,0,0.1);
    border: 1.5px solid rgba(240,196,0,0.4);
    border-radius: 12px;
    padding: 14px 18px;
    font-size: 0.88rem;
    color: #5a4a3a;
    line-height: 1.65;
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .rp-note-label {
    font-weight: 900;
    color: #8a6800;
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* ── Damaged card ── */
  .rp-damaged-card {
    background: linear-gradient(135deg, rgba(79,126,23,0.06) 0%, rgba(245,209,157,0.18) 100%);
    border-left: 4px solid var(--citrusy);
    border-radius: 0 14px 14px 0;
    padding: 20px 22px;
    font-size: 0.95rem;
    color: #5a4a3a;
    line-height: 1.75;
  }

  /* ── Footer Note ── */
  .rp-footer-note {
    background: linear-gradient(135deg, rgba(79,126,23,0.06), rgba(240,196,0,0.08));
    border: 1.5px solid var(--edam-dark);
    border-radius: 18px;
    padding: 28px 32px;
    text-align: center;
    font-size: 0.97rem;
    color: #6a5a48;
    line-height: 1.7;
    position: relative;
    overflow: hidden;
    margin-top: 40px;
  }

  .rp-footer-note::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--green), var(--citrusy), var(--sunburst));
    border-radius: 18px 18px 0 0;
  }

  .rp-footer-note-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.1rem;
    font-weight: 700;
    font-style: italic;
    color: var(--soil);
    margin-bottom: 8px;
  }

  /* ── Loading ── */
  .rp-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 18px;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.5;transform:scale(0.85);} }

  .rp-spinner-wrap { position: relative; width: 56px; height: 56px; }
  .rp-spinner {
    position: absolute; inset: 0;
    border-radius: 50%;
    border: 3px solid rgba(79,126,23,0.15);
    border-top-color: var(--green);
    animation: spin 0.8s linear infinite;
  }
  .rp-spinner-inner {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%,-50%);
    width: 24px; height: 24px;
    background: var(--sunburst);
    border-radius: 50%;
    opacity: 0.7;
    animation: pulse 1.2s ease-in-out infinite;
  }
  .rp-loading-text {
    font-size: 0.92rem; font-weight: 700;
    color: var(--green); letter-spacing: 0.06em;
    animation: pulse 1.2s ease-in-out infinite;
  }

  /* ── Error ── */
  .rp-error-wrap {
    display: flex; align-items: center; justify-content: center; min-height: 60vh;
  }
  .rp-error-box {
    background: rgba(205,3,2,0.05);
    border: 1.5px solid rgba(205,3,2,0.25);
    border-radius: 16px;
    padding: 32px 40px;
    text-align: center;
    max-width: 420px;
  }
  .rp-error-icon { color: var(--russian-red); margin: 0 auto 14px; display: block; }
  .rp-error-text { font-size: 0.97rem; color: var(--russian-red); font-weight: 700; }

  /* ── Scroll animation ── */
  .rp-anim {
    opacity: 0;
    transform: translateY(16px);
    transition: opacity 0.65s ease-out, transform 0.65s ease-out;
  }
  .rp-anim.visible {
    opacity: 1;
    transform: none;
  }
`;

/* ── AnimatedOnScroll (unchanged logic) ── */
const AnimatedOnScroll = ({ children, animationClass, delay = 0 }) => {
  const domRef = useRef();
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setVisible(true);
        observer.unobserve(domRef.current);
      }
    }, { threshold: 0.15 });

    if (domRef.current) observer.observe(domRef.current);

    return () => {
      if (domRef.current) observer.unobserve(domRef.current);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`rp-anim ${isVisible ? 'visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

/* ── RefundPolicyPage ── */
const RefundPolicyPage = () => {
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReturnPolicy = async () => {
      try {
        const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/api/return-policy');
        const data = await response.json();
        if (data.success) {
          setPolicyData(data.policy);
        } else {
          setError(data.error || 'Failed to load return policy');
        }
      } catch (err) {
        setError('Failed to load return policy');
        console.error('Error fetching return policy:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReturnPolicy();
  }, []);

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="rp-root">
          <div className="rp-loading">
            <div className="rp-spinner-wrap">
              <div className="rp-spinner" />
              <div className="rp-spinner-inner" />
            </div>
            <p className="rp-loading-text">Loading return policy...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <style>{styles}</style>
        <div className="rp-root">
          <div className="rp-error-wrap">
            <div className="rp-error-box">
              <svg className="rp-error-icon" width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="rp-error-text">{error}</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="rp-root">
        <div className="rp-inner">

          {/* ── Header ── */}
          <AnimatedOnScroll animationClass="rp-anim visible">
            <div className="rp-header">
              <div className="rp-header-deco" />
              <div className="rp-eyebrow">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H7l5-8v4h4l-5 8z"/>
                </svg>
                Policies
              </div>
              <h1 className="rp-title">
                {policyData?.title
                  ? <>{policyData.title.split(' ').slice(0, -1).join(' ')} <span>{policyData.title.split(' ').slice(-1)}</span></>
                  : <>Return <span>Policy</span></>
                }
              </h1>
              <p className="rp-updated">
                🌿 {policyData?.lastUpdated || 'Last Updated: January 2026'}
              </p>
            </div>
          </AnimatedOnScroll>

          {/* ── Introduction ── */}
          <AnimatedOnScroll delay={80}>
            <div className="rp-section">
              <div className="rp-intro-strip">
                {policyData?.introduction || 'At "Elevate Mart", we value your shopping experience. If you are not satisfied with your purchase, you can return or exchange the product following our return policy.'}
              </div>
            </div>
          </AnimatedOnScroll>

          {/* ── Return Timeline ── */}
          <AnimatedOnScroll delay={120}>
            <div className="rp-section">
              <div className="rp-section-header">
                <div className="rp-section-icon orange">📅</div>
                <h2 className="rp-section-title">Return Timeline</h2>
              </div>
              <div className="rp-timeline-strip">
                <p dangerouslySetInnerHTML={{
                  __html: policyData?.returnTimeline ||
                    'You can apply for a return or exchange within <strong style="color:#F88702">7 (seven) days</strong> from the delivery date. No requests will be accepted after this period.'
                }} />
              </div>
            </div>
          </AnimatedOnScroll>

          {/* ── Return Conditions ── */}
          <AnimatedOnScroll delay={160}>
            <div className="rp-section">
              <div className="rp-section-header">
                <div className="rp-section-icon green">📋</div>
                <h2 className="rp-section-title">Return Conditions</h2>
              </div>
              <div className="rp-conditions">
                {policyData?.conditions && policyData.conditions.length > 0 ? (
                  policyData.conditions.map((condition, index) => (
                    <AnimatedOnScroll key={index} delay={180 + index * 50}>
                      <div className={`rp-condition ${condition.type === 'allowed' ? 'allowed' : 'denied'}`}>
                        <div className="rp-condition-icon">
                          {condition.type === 'allowed' ? '✓' : '✕'}
                        </div>
                        <p className="rp-condition-text">{condition.text}</p>
                      </div>
                    </AnimatedOnScroll>
                  ))
                ) : (
                  <>
                    {[
                      { type: 'allowed', text: 'The product must be unused and in its original condition.' },
                      { type: 'allowed', text: 'You must record a video while unboxing the product. If there are any issues, send this video to our WhatsApp number and return within 7 days, otherwise Elevate Mart will not accept it.' },
                      { type: 'allowed', text: 'All tags, labels, and original packaging must remain intact with the product.' },
                      { type: 'allowed', text: 'You must provide proof of purchase receipt or order confirmation.' },
                      { type: 'denied', text: 'Personal care products, innerwear, and certain specific items are non-returnable for hygiene reasons.' },
                      { type: 'denied', text: 'Returns will not be accepted if you don\'t like the product after receiving it. Please review photos and videos carefully before purchasing. Elevate Mart provides products with photos and videos.' },
                    ].map((item, index) => (
                      <AnimatedOnScroll key={index} delay={180 + index * 50}>
                        <div className={`rp-condition ${item.type === 'allowed' ? 'allowed' : 'denied'}`}>
                          <div className="rp-condition-icon">
                            {item.type === 'allowed' ? '✓' : '✕'}
                          </div>
                          <p className="rp-condition-text">{item.text}</p>
                        </div>
                      </AnimatedOnScroll>
                    ))}
                  </>
                )}
              </div>
            </div>
          </AnimatedOnScroll>

          {/* ── Return Process ── */}
          <AnimatedOnScroll delay={200}>
            <div className="rp-section">
              <div className="rp-section-header">
                <div className="rp-section-icon green">🔄</div>
                <h2 className="rp-section-title">Return Process</h2>
              </div>
              <AnimatedOnScroll delay={230}>
                <div className="rp-process-card">
                  {policyData?.returnProcess || 'To apply for a return, please contact our customer service team. Mention your order number and the reason for return. We will review your request and inform you about the next steps.'}
                </div>
              </AnimatedOnScroll>
              <AnimatedOnScroll delay={260}>
                <div className="rp-note-card">
                  <span className="rp-note-label">⚡ Note:</span>
                  <span>Return shipping costs are generally borne by the customer, unless the product is defective or incorrect.</span>
                </div>
              </AnimatedOnScroll>
            </div>
          </AnimatedOnScroll>

          {/* ── Processing Time ── */}
          <AnimatedOnScroll delay={240}>
            <div className="rp-section">
              <div className="rp-section-header">
                <div className="rp-section-icon orange">⏱</div>
                <h2 className="rp-section-title">Return Processing Time</h2>
              </div>
              <AnimatedOnScroll delay={270}>
                <div className="rp-timeline-strip">
                  <p dangerouslySetInnerHTML={{
                    __html: policyData?.processingTime ||
                      'Once we receive and inspect the returned product, your refund process may take <strong style="color:#F88702">5-7 business days</strong> to complete. The refund method will depend on your original payment method.'
                  }} />
                </div>
              </AnimatedOnScroll>
            </div>
          </AnimatedOnScroll>

          {/* ── Damaged / Defective ── */}
          <AnimatedOnScroll delay={280}>
            <div className="rp-section">
              <div className="rp-section-header">
                <div className="rp-section-icon red">⚠️</div>
                <h2 className="rp-section-title">Damaged or Defective Products</h2>
              </div>
              <AnimatedOnScroll delay={310}>
                <div className="rp-damaged-card">
                  <p dangerouslySetInnerHTML={{
                    __html: policyData?.damagedProducts ||
                      'If you receive a damaged or defective product, please inform us within <strong style="color:#CD0302">48 hours</strong> of delivery. In such cases, we will arrange a replacement product or a full refund for you.'
                  }} />
                </div>
              </AnimatedOnScroll>
            </div>
          </AnimatedOnScroll>

          {/* ── Footer Note ── */}
          <AnimatedOnScroll delay={320}>
            <div className="rp-footer-note">
              <p className="rp-footer-note-title">🌿 Have Questions?</p>
              <p>
                {policyData?.footerNote || 'If you have any questions about our return policy, please feel free to contact us.'}
              </p>
            </div>
          </AnimatedOnScroll>

        </div>
      </div>
    </>
  );
};

export default RefundPolicyPage;